// Attachment validation for the create-ticket modal — type (image/video/pdf)
// + size. Pure + dependency-free so it's unit-testable without a real
// File/upload. Mirrors apps/incidents/web/.../validate-attachment.ts, with
// document (PDF) support added per the support-ticket attachment requirement
// (screenshots/video/allowed documents) — the shared `fileUploader` route
// (packages/uploadthing/src/server/file-router.ts) already accepts exactly
// image (16MB) / pdf (16MB) / video (256MB), so these caps match the server.

export const MAX_IMAGE_BYTES = 16 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 256 * 1024 * 1024;
export const MAX_PDF_BYTES = 16 * 1024 * 1024;

export const isVideoName = (name: string): boolean =>
  /\.(mp4|mov|webm|avi|mkv|m4v)$/i.test(name);

export const isImageName = (name: string): boolean =>
  /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif)$/i.test(name);

export const isPdfName = (name: string): boolean => /\.pdf$/i.test(name);

type FileLike = { name: string; type: string; size: number };

// Returns an error message when the file is not an acceptable attachment, or
// null when it is (image ≤ 16MB, video ≤ 256MB, or PDF ≤ 16MB).
export const validateAttachmentFile = (file: FileLike): string | null => {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const isPdf = file.type === 'application/pdf';
  if (!(isImage || isVideo || isPdf)) {
    return `${file.name}: only images, video, or PDF files are allowed.`;
  }
  if (isImage && file.size > MAX_IMAGE_BYTES) {
    return `${file.name}: image too large (max 16MB).`;
  }
  if (isVideo && file.size > MAX_VIDEO_BYTES) {
    return `${file.name}: video too large (max 256MB).`;
  }
  if (isPdf && file.size > MAX_PDF_BYTES) {
    return `${file.name}: PDF too large (max 16MB).`;
  }
  return null;
};
